import { Box, useToken } from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FaTruck } from 'react-icons/fa';
import { IoEyeOutline } from 'react-icons/io5';
import { MdEdit, MdOutlineRepeat, MdSupportAgent } from 'react-icons/md';
import { useSearchParams } from 'react-router';
import FaceliftDataLoader from '@/components/Atoms/FaceliftDataLoader';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import FaceliftTruncatedTextWithTooltip from '@/components/Atoms/TruncatedTextWithTooltip';
import FaceliftDialogWithExitConfirmation from '@/components/Molecules/FaceliftDialogWithExitConfirmation';
import type { ExitConfirmationDraftAction } from '@/components/Molecules/FaceliftDialogWithExitConfirmation';
import Show from '@/components/Molecules/Show';
import DataTable from '@/components/Organisms/DataTable';
import type {
	ColumnData,
	onFilterApplyProps,
} from '@/components/Organisms/DataTable/DataTable.types';
import DetailsView from '@/components/Templates/DetailsView';
import { BulkOrderRequestForm } from '@/components/Templates/Requests/BulkOrderRequestForm';
import { OrderRequestForm } from '@/components/Templates/Requests/OrderRequestForm';
import { parseOrderDetailsForModal } from '@/components/Templates/Requests/utils';
import { SupportTicketForm } from '@/components/Templates/SupportTickets/SupportTicketForm';
import { allowedPortForProducts } from '@/constants/clinic-port';
import { useGetProductClinicPort } from '@/hooks/service-hooks/common';
import {
	useGetOrderById,
	useGetOrderShipments,
	useGetRequestProducts,
	useGetRequests,
} from '@/hooks/service-hooks/requests';
import { useRequestsTutorial } from '@/hooks/tutorialsHooks/useRequestsTutorial';
import { useTutorialActive } from '@/hooks/tutorialsHooks/useTutorialActive';
import { useAppTour } from '@/hooks/useAppTour';
import { useClinicFilter } from '@/hooks/useClinicFilter';
import usePermission from '@/hooks/usePermission';
import { getRequests } from '@/services/requests';
import { useAuthStore } from '@/store';
import type { FilterType } from '@/types/table';
import { formatDate, parseUserFriendlyError } from '@/utils';
import { trackCoreClick } from '@/utils/analytics';
import {
	generateCSVFromTable,
	generateExcelFromTable,
} from '@/utils/exportFiles';
import { generateMockRequestData } from '@/utils/tutorials/mockData';
import {
	columns,
	getOrderType,
	getRequestStatus,
	statusFilterOptions,
} from './requests';
import ShipmentModalContent from './ShipmentModalContent';

// Small wrappers so export helpers can reliably read text via `props.text`
const StatusTextWrapper = ({ text }: { text: string }) => <>{text}</>;

const SimpleTextCell = ({ text }: { text: string }) => (
	<Box display="inline-flex" alignItems="center" fontWeight={500} fontSize="sm">
		{text}
	</Box>
);

const CLINIC_KEY = '_ta_clinic_value';
const PRODUCT_KEY = 'ta_SKU.ta_qcode';

const RequestsTable = () => {
	const [brand600] = useToken('colors', ['brand.600']);
	const { hasPermission } = usePermission();
	const { isRequestsTutorialActive } = useTutorialActive();

	// Shared clinic filter helpers
	const { defaultClinicIds, mapGlobalFilters } = useClinicFilter(CLINIC_KEY);

	const user = useAuthStore((state) => state.user?.userInformation);
	const ENABLE_PRODUCT_BY_PORT = ['Mindsight', 'SalesRep'].includes(
		user?.portsAvailable || '',
	);

	const [
		requestsTableRef,
		addButtonRequestsTableRef,
		exportButtonRequestsTableRef,
		ordersActionHamburgerRef,
	] = [
			useRef<HTMLDivElement | null>(null),
			useRef<HTMLButtonElement | null>(null),
			useRef<HTMLButtonElement | null>(null),
			useRef<HTMLElement | null>(null),
		];

	// Use the Requests Tutorial hook to manage all tutorial logic
	useRequestsTutorial({
		requestsTableRef,
		ordersActionHamburgerRef,
	});

	useAppTour({
		elements: {
			requestsTableRef: requestsTableRef.current,
			addButtonRequestsTableRef: addButtonRequestsTableRef.current,
			exportButtonRequestsTableRef: exportButtonRequestsTableRef.current,
		},
	});

	// Simple modal state management - like BenefitVerificationTable
	const [modalInformation, setModalInformation] = useState<{
		mode:
		| 'createOrder'
		| 'editOrder'
		| 'createBulk'
		| 'editBulk'
		| 'reorderOrder'
		| 'reorderBulk'
		| 'viewDetails'
		| 'viewShipment'
		| 'supportTicket'
		| '';
		orderId?: string; // For edit mode, reorder mode, or view details
		clinicData?: { value: string; label: string } | null; // For support ticket
		orderName?: string; // For support ticket subject
	}>({
		mode: '',
	});

	const [isFormDirty, setIsFormDirty] = useState(false);
	const [isBulkFormDirty, setIsBulkFormDirty] = useState(false);
	const [isSupportTicketFormDirty, setIsSupportTicketFormDirty] = useState(false);
	const [orderExitDraftAction, setOrderExitDraftAction] =
		useState<ExitConfirmationDraftAction | null>(null);
	const [bulkOrderExitDraftAction, setBulkOrderExitDraftAction] =
		useState<ExitConfirmationDraftAction | null>(null);
	const [supportTicketExitDraftAction, setSupportTicketExitDraftAction] =
		useState<ExitConfirmationDraftAction | null>(null);
	const [products, setProducts] = useState<{ id: string; label: string }[]>([]);

	const { data: portProductData, isFetching: isPortProductFetching } = useGetProductClinicPort({
		clinicIds: defaultClinicIds,
		port: user?.portsAvailable || '',
		enabled: ENABLE_PRODUCT_BY_PORT,
	});

	// Initialize with DataTable's default values and dynamic clinic filters
	const [filters, setFilters] = useState<FilterType>({
		page: 1,
		limit: 15,
		columnsFilters: '[]',
		search: '',
		enabled: defaultClinicIds.length > 0 &&
			(!!portProductData || !ENABLE_PRODUCT_BY_PORT),
		status: '',
		globalFilters: JSON.stringify(
			[
				...(defaultClinicIds.length ? [{
					key: CLINIC_KEY,
					values: defaultClinicIds,
				}] : []),
				...(ENABLE_PRODUCT_BY_PORT && portProductData?.length ? [{
					key: PRODUCT_KEY,
					values:
						portProductData?.flatMap((p) =>
							(
								p[
								allowedPortForProducts.OrderRequest.by as keyof typeof p
								] as string
							)?.split(','),
						) || [],
				}] : []),
			]
		),
		sort: '',
	});

	const { data, isFetching, refetch } = useGetRequests(filters);

	const { data: productsData, isFetching: isProductsFetching } = useGetRequestProducts({
		...filters,
		globalFilters: ENABLE_PRODUCT_BY_PORT
			? JSON.stringify([
				...JSON.parse(filters.globalFilters || '[]'),
				{
					key: PRODUCT_KEY,
					values:
						portProductData?.flatMap((p) =>
							(
								p[
								allowedPortForProducts.OrderRequest.by as keyof typeof p
								] as string
							).split(','),
						) || [],
				},
			])
			: filters.globalFilters,
		limit: 9999,
		enabled: defaultClinicIds.length > 0 &&
			products?.length === 0 && (!!portProductData || !ENABLE_PRODUCT_BY_PORT),
	});

	useEffect(() => {
		if (productsData?.items && !products.length) {
			setProducts(
				productsData.items.map((item) => ({
					id: item.qcode,
					label: `${item.productName} (${item.qcode})`,
				})),
			);

			const productCodes = ENABLE_PRODUCT_BY_PORT && portProductData?.length
				? portProductData[0].portProducts?.split(',').filter(Boolean) || []
				: [];

			// Update filters state to trigger API call
			setFilters({
				...filters,
				enabled: defaultClinicIds.length > 0 && (!!portProductData || !ENABLE_PRODUCT_BY_PORT),
				globalFilters: JSON.stringify([
					{
						key: CLINIC_KEY,
						values: defaultClinicIds,
					},
					...(ENABLE_PRODUCT_BY_PORT && productCodes.length ? [{
						key: PRODUCT_KEY,
						values: productCodes,
					}] : []),
				])
			});
		}
	}, [productsData]);
	// Check URL params for order ID to open modal
	const [searchParams, setSearchParams] = useSearchParams();
	const orderIdFromUrl = searchParams.get('orderId');

	// Open modal if orderId is in URL
	useEffect(() => {
		if (orderIdFromUrl && modalInformation.mode !== 'viewDetails') {
			setModalInformation({ mode: 'viewDetails', orderId: orderIdFromUrl });
			// Remove the query parameter from URL after opening modal
			const newSearchParams = new URLSearchParams(searchParams);
			newSearchParams.delete('orderId');
			setSearchParams(newSearchParams, { replace: true });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orderIdFromUrl]);

	// Get individual order data for details modal
	const { data: orderDetails, isFetching: isOrderDetailsLoading } =
		useGetOrderById(
			modalInformation.mode === 'viewDetails'
				? (modalInformation.orderId as string)
				: null,
			modalInformation.mode === 'viewDetails',
		);

	// Get order shipment data for shipment tracking modal
	const { data: orderShipments, isFetching: isOrderShipmentsLoading } =
		useGetOrderShipments(
			modalInformation.mode === 'viewShipment'
				? (modalInformation.orderId as string)
				: null,
			modalInformation.mode === 'viewShipment',
		);

	// Transform API data to table format
	const transformedData = useMemo(() => {
		// IMPORTANT: Only use mock data when the tutorial is ACTIVE
		// When tutorial is NOT active, use real API data
		if (isRequestsTutorialActive) {
			// Tutorial is active - use mock data
			const mockData = generateMockRequestData(10);
			return mockData.map((item: any) => {
				const status = getRequestStatus(item.ta_status);
				const type = getOrderType(item._ta_bvid_value);
				const productName = item.ta_SKU?.ta_name || '-';
				const doctorName = item.ta_Doctor?.fullname || '-';
				const patientName = item.ta_BVPatient?.fullname || '-';
				const shippingLocation = item.ta_ShippingLocation?.ta_addressline || '-';
				const clinicName = item.ta_Clinic?.name || '-';

				return {
					ta_orderrequestid: item.ta_orderrequestid,
					id: item.ta_orderrequestid,
					type: <SimpleTextCell text={type} />,
					'ta_Clinic.name': (
						<FaceliftTruncatedTextWithTooltip text={clinicName} maxLength={30} />
					),
					ta_clinicpo: (
						<FaceliftTruncatedTextWithTooltip
							text={item.ta_clinicpo || '-'}
							maxLength={20}
						/>
					),
					'ta_Doctor.fullname': (
						<FaceliftTruncatedTextWithTooltip text={doctorName} maxLength={25} />
					),
					'ta_BVPatient.fullname':
						type === 'Individual' ? (
							<FaceliftTruncatedTextWithTooltip
								text={patientName}
								maxLength={25}
							/>
						) : (
							'-'
						),
					'ta_SKU.ta_name': (
						<FaceliftTruncatedTextWithTooltip text={productName} maxLength={30} />
					),
					'order.ta_quantity': item.ta_quantity || '-',
					ta_orderrequestdate: formatDate(item.ta_orderrequestdate),
					ta_dos: formatDate(item.ta_dos),
					'ta_ShippingLocation.ta_addressline': (
						<FaceliftTruncatedTextWithTooltip
							text={shippingLocation}
							maxLength={35}
						/>
					),
					ta_status: (
						<Box
							bg={status.color}
							borderRadius={4}
							color="white"
							height="24px"
							px={3}
							display="flex"
							alignItems="center"
							justifyContent="center"
							fontWeight={500}
							fontSize="sm"
						>
							<StatusTextWrapper text={status.label} />
						</Box>
					),
					// Store the raw status for action filtering
					_statusCode: item.ta_status,
					// Store shipments data for action filtering
					_hasShipments: item.shipments && item.shipments.length > 0,
				};
			});
		}

		if (!data?.items) return [];

		return data.items.map((item: any) => {
			const status = getRequestStatus(item.ta_status);
			const type = getOrderType(item._ta_bvid_value);
			const productName = item.ta_SKU?.ta_name || '-';
			const doctorName = item.ta_Doctor?.fullname || '-';
			const patientName = item.ta_BVPatient?.fullname || '-';
			const shippingLocation = item.ta_ShippingLocation?.ta_addressline || '-';
			const clinicName = item.ta_Clinic?.name || '-';

			return {
				ta_orderrequestid: item.ta_orderrequestid,
				id: item.ta_orderrequestid,
				type: <SimpleTextCell text={type} />,
				'ta_Clinic.name': (
					<FaceliftTruncatedTextWithTooltip text={clinicName} maxLength={30} />
				),
				ta_clinicpo: (
					<FaceliftTruncatedTextWithTooltip
						text={item.ta_clinicpo || '-'}
						maxLength={20}
					/>
				),
				'ta_Doctor.fullname': (
					<FaceliftTruncatedTextWithTooltip text={doctorName} maxLength={25} />
				),
				'ta_BVPatient.fullname':
					type === 'Individual' ? (
						<FaceliftTruncatedTextWithTooltip
							text={patientName}
							maxLength={25}
						/>
					) : (
						'-'
					),
				'ta_SKU.ta_name': (
					<FaceliftTruncatedTextWithTooltip text={productName} maxLength={30} />
				),
				'order.ta_quantity': item.ta_quantity || '-',
				ta_orderrequestdate: formatDate(item.ta_orderrequestdate),
				ta_dos: formatDate(item.ta_dos),
				'ta_ShippingLocation.ta_addressline': (
					<FaceliftTruncatedTextWithTooltip
						text={shippingLocation}
						maxLength={35}
					/>
				),
				ta_status: (
					<Box
						bg={status.color}
						borderRadius={4}
						color="white"
						height="24px"
						px={3}
						display="flex"
						alignItems="center"
						justifyContent="center"
						fontWeight={500}
						fontSize="sm"
					>
						<StatusTextWrapper text={status.label} />
					</Box>
				),
				// Store the raw status for action filtering
				_statusCode: item.ta_status,
				// Store shipments data for action filtering
				_hasShipments: item.shipments && item.shipments.length > 0,
			};
		});
	}, [data, isRequestsTutorialActive]);

	// Handle filter apply
	const onFilterApply = ({
		page,
		limit,
		status,
		search = '',
		sort = null,
		columnsFilters = [],
		globalFilters = [],
	}: onFilterApplyProps) => {
		const statusValue = status?.value?.toString() || '';
		const globalFilter = mapGlobalFilters(globalFilters)
		const productGlobalFilter = globalFilter.find(
			(filter) => filter.key === PRODUCT_KEY,
		);
		const productValuesFromFilter = productGlobalFilter
			? productGlobalFilter.values
			: [];

		// If product filter is applied, use those values
		// Otherwise, fall back to port-based products if enabled
		const portBasedProductValues =
			ENABLE_PRODUCT_BY_PORT && portProductData?.length
				? portProductData[0].portProducts?.split(',').filter(Boolean) || []
				: [];

		const finalProductValues =
			productValuesFromFilter.length > 0
				? productValuesFromFilter
				: portBasedProductValues;

		const clinicGlobalFilter = globalFilter.find(
			(filter) => filter.key === CLINIC_KEY,
		);
		
		const clinicValuesFromFilter = clinicGlobalFilter
			? clinicGlobalFilter.values
			: [];
		const finalClinicIds = clinicValuesFromFilter.length > 0
			? clinicValuesFromFilter
			: defaultClinicIds;

		// Add the final clinic and product filters in setFilters
		const productCodes = finalProductValues;
		const finalGlobalFilters = [
			{
				key: CLINIC_KEY,
				values: finalClinicIds,
			},
			...(productCodes.length ? [{
				key: PRODUCT_KEY,
				values: productCodes,
			}] : []),
		];

		// Update filters state to trigger API call
		setFilters({
			page,
			limit,
			columnsFilters: JSON.stringify(columnsFilters),
			search,
			enabled: defaultClinicIds.length > 0,
			status: statusValue,
			globalFilters: JSON.stringify(finalGlobalFilters),
			sort: sort ? `${sort.key}|${sort.direction}` : '',
		});
	};

	// Simple modal handlers - like BenefitVerificationTable
	const handleCreateRequest = () => {
		trackCoreClick('create_order_clicked', {
			source_module: 'requests_table',
			order_kind: 'patient',
		});
		setModalInformation({ mode: 'createOrder' });
	};

	const handleCreateBulkRequest = () => {
		trackCoreClick('create_order_clicked', {
			source_module: 'requests_table',
			order_kind: 'clinic',
		});
		setModalInformation({ mode: 'createBulk' });
	};

	// Transform data for export (without JSX elements)
	const transformedDataToExport = (items: any[]) => {
		if (!items) return [];

		return items.map((item: any) => {
			const status = getRequestStatus(item.ta_status);
			const type = getOrderType(item._ta_bvid_value);
			const productName = item.ta_SKU?.ta_name || '-';
			const doctorName = item.ta_Doctor?.fullname || '-';
			const patientName = item.ta_BVPatient?.fullname || '-';
			const shippingLocation = item.ta_ShippingLocation?.ta_addressline || '-';
			const clinicName = item.ta_Clinic?.name || '-';

			return {
				ta_orderrequestid: item.ta_orderrequestid,
				id: item.ta_orderrequestid,
				type: type,
				'ta_Clinic.name': clinicName,
				ta_clinicpo: item.ta_clinicpo || '-',
				'ta_Doctor.fullname': doctorName,
				'ta_BVPatient.fullname': type === 'Individual' ? patientName : '-',
				'ta_SKU.ta_name': productName,
				'order.ta_quantity': item.ta_quantity || '-',
				ta_orderrequestdate: formatDate(item.ta_orderrequestdate),
				ta_dos: formatDate(item.ta_dos),
				'ta_ShippingLocation.ta_addressline': shippingLocation,
				ta_status: status.label,
			};
		});
	};

	// Export functions
	const exportAllAsCSV = async () => {
		try {
			// Fetch all data with current filters
			const response = await getRequests(
				1,
				9999,
				filters.columnsFilters,
				filters.search,
				String(filters.status || ''),
				filters.globalFilters,
				String(filters.sort || ''),
			);

			const transformedData = transformedDataToExport(response?.items || []);
			generateCSVFromTable(columns, transformedData, 'Requests-all', true);
		} catch (error) {
			console.error('Error exporting CSV:', error);
			const userFriendlyMessage = parseUserFriendlyError(
				error,
				'Unable to export data. Please try again.',
			);
			toast.error(userFriendlyMessage);
		}
	};

	const exportAllAsExcel = async () => {
		try {
			// Fetch all data with current filters
			const response = await getRequests(
				1,
				9999,
				filters.columnsFilters,
				filters.search,
				String(filters.status || ''),
				filters.globalFilters,
				String(filters.sort || ''),
			);

			const transformedData = transformedDataToExport(response?.items || []);
			generateExcelFromTable(columns, transformedData, 'Requests-all', true);
		} catch (error) {
			console.error('Error exporting Excel:', error);
			const userFriendlyMessage = parseUserFriendlyError(
				error,
				'Unable to export data. Please try again.',
			);
			toast.error(userFriendlyMessage);
		}
	};

	// Handle edit request - simple like BenefitVerificationTable
	const handleEditRequest = (id: string) => {
		// Find the item to determine if it's bulk or single
		const item = data?.items?.find((req: any) => req.ta_orderrequestid === id);
		if (!item) return;

		const isBulkOrder = !item._ta_bvid_value; // Bulk orders don't have BV ID
		trackCoreClick('edit_order_clicked', {
			source_module: 'requests_table',
			order_id: id,
			order_kind: isBulkOrder ? 'clinic' : 'patient',
		});

		// Simply set the modal mode and orderId - form will fetch its own data
		setModalInformation({
			mode: isBulkOrder ? 'editBulk' : 'editOrder',
			orderId: id,
		});
	};

	// Handle reorder request - similar to edit but for creating a new order
	const handleReorderRequest = (id: string) => {
		// Find the item to determine if it's bulk or single
		const item = data?.items?.find((req: any) => req.ta_orderrequestid === id);
		if (!item) return;

		const isBulkOrder = !item._ta_bvid_value; // Bulk orders don't have BV ID
		trackCoreClick('reorder_clicked', {
			source_module: 'requests_table',
			order_id: id,
			order_kind: isBulkOrder ? 'clinic' : 'patient',
		});

		// Set the modal mode to reorder and orderId - form will fetch and use data for create
		setModalInformation({
			mode: isBulkOrder ? 'reorderBulk' : 'reorderOrder',
			orderId: id,
		});
	};

	// Define actions with conditional visibility based on status
	const actions = [
		// Always visible - View Details (first action)
		{
			label: 'Details',
			icon: <IoEyeOutline />,
			actionFn: (item: ColumnData) => {
				const originalItem = data?.items?.find(
					(req: any) => req.ta_orderrequestid === item.id,
				);
				trackCoreClick('view_order_details_clicked', {
					source_module: 'requests_table',
					order_id: item.id as string,
					order_kind: originalItem
						? originalItem._ta_bvid_value
							? 'patient'
							: 'clinic'
						: '',
				});
				setModalInformation({
					mode: 'viewDetails',
					orderId: item.id as string,
				});
			},
		},
		// Open status actions
		{
			label: 'Edit',
			icon: <MdEdit />,
			actionFn: (item: ColumnData) => {
				handleEditRequest(item.id as string);
			},
			show: (item: any) =>
				hasPermission(['Orders:Edit']) &&
				item._statusCode !== 953870001 &&
				item._statusCode !== 953870002 &&
				item._statusCode !== 953870004 &&
				item._statusCode !== 953870006,
		},
		// View Shipment Details - show when shipments exist
		{
			label: 'View Shipment Details',
			icon: <FaTruck />,
			actionFn: (item: ColumnData) => {
				const originalItem = data?.items?.find(
					(req: any) => req.ta_orderrequestid === item.id,
				);
				trackCoreClick('view_shipment_clicked', {
					source_module: 'requests_table',
					order_id: item.id as string,
					order_kind: originalItem
						? originalItem._ta_bvid_value
							? 'patient'
							: 'clinic'
						: '',
				});
				setModalInformation({
					mode: 'viewShipment',
					orderId: item.id as string,
				});
			},
			show: (item: any) => item._hasShipments,
		},

		// Open status actions
		{
			label: 'Support Ticket',
			icon: <MdSupportAgent />,
			actionFn: (item: ColumnData) => {
				// Find the original data to get clinic information
				const originalItem = data?.items?.find(
					(req: any) => req.ta_orderrequestid === item.id,
				);
				const clinic = originalItem?.ta_Clinic;
				const orderName = originalItem?.ta_name || '';

				setIsSupportTicketFormDirty(false);
				setModalInformation({
					mode: 'supportTicket',
					orderId: item.id as string,
					clinicData:
						clinic && clinic.accountid && clinic.name
							? { value: clinic.accountid, label: clinic.name }
							: null,
					orderName: orderName,
				});
			},
			show: () => hasPermission(['SupportTickets:Create']),
		},
		// Closed status actions

		{
			label: 'Reorder',
			icon: <MdOutlineRepeat />,
			actionFn: (item: ColumnData) => {
				handleReorderRequest(item.id as string);
			},
			show: (item: any) =>
				hasPermission(['Orders:Create']) && item._statusCode === 953870001,
		},
	];

	return (
		<Box
			p={{ base: 4, md: 8 }}
			bg={{ base: '{colors.gray.50}', _dark: '{colors.gray.900}' }}
			position="relative"
		>
			<DataTable
				tableName="Order Status"
				moduleName="Order Request"
				ref={requestsTableRef}
				addButtonRef={addButtonRequestsTableRef}
				exportButtonRef={exportButtonRequestsTableRef}
				loading={isFetching || isProductsFetching || isPortProductFetching}
				items={transformedData}
				columns={columns}
				topLabel="Orders status"
				totalCount={data?.meta.totalRecords || 0}
				onFilterApply={onFilterApply}
				searchable
				hideShowColumn
				expandable
				paginated
				fullscreen
				exportable
				bordered
				globalFilter={{
					title: 'Filters',
					clinics: true,
					items: [
						{
							name: 'Products',
							key: PRODUCT_KEY,
							options: products,
						},
					],
				}}
				{...(hasPermission(['Orders:Create']) && {
					creatableFn: [
						{ label: 'Create Patient Order', action: handleCreateRequest },
						{ label: 'Create Clinic Order', action: handleCreateBulkRequest },
					],
				})}
				statusFilters={statusFilterOptions}
				actions={actions}
				csv
				excel
				// pdf
				exportAllAsCSV={exportAllAsCSV}
				exportAllAsExcel={exportAllAsExcel}
			// exportAllAsPDF={exportAllAsPDF}
			/>

			{/* Single Order Modal */}
			<FaceliftDialogWithExitConfirmation
				open={
					modalInformation.mode === 'createOrder' ||
					modalInformation.mode === 'editOrder' ||
					modalInformation.mode === 'reorderOrder'
				}
				onOpenChange={(openState) => {
					if (!openState) {
						setModalInformation({ mode: '' });
					}
				}}
				title={
					modalInformation.mode === 'createOrder'
						? 'Create Patient Order'
						: modalInformation.mode === 'reorderOrder'
							? 'Reorder Patient Order'
							: 'Edit Patient Order'
				}
				showBackBtn={true}
				size="xl"
				sx={{
					boxShadow: `0 0 20px 0px ${brand600}`,
					width: '840px',
					maxWidth: '95vw',
					// height: '565px',
					// maxHeight: '90vh',
					overflowY: 'auto',
				}}
				isDirty={isFormDirty}
				saveDraftAction={orderExitDraftAction}
			>
				<OrderRequestForm
					mode={
						modalInformation.mode === 'editOrder'
							? 'edit'
							: modalInformation.mode === 'reorderOrder'
								? 'reorder'
								: 'create'
					}
					orderId={modalInformation.orderId}
					onSuccess={() => {
						setModalInformation({ mode: '' });
						refetch();
					}}
					onIsDirtyChange={setIsFormDirty}
					onExitConfirmationDraftActionChange={
						['createOrder', 'editOrder', 'reorderOrder'].includes(
							modalInformation.mode,
						)
							? setOrderExitDraftAction
							: undefined
					}
				/>
			</FaceliftDialogWithExitConfirmation>

			{/* Bulk Order Modal */}
			<FaceliftDialogWithExitConfirmation
				open={
					modalInformation.mode === 'createBulk' ||
					modalInformation.mode === 'editBulk' ||
					modalInformation.mode === 'reorderBulk'
				}
				onOpenChange={(openState) => {
					if (!openState) {
						setModalInformation({ mode: '' });
					}
				}}
				title={
					modalInformation.mode === 'createBulk'
						? 'Create Clinic Order'
						: modalInformation.mode === 'reorderBulk'
							? 'Reorder Bulk Order'
							: 'Edit Bulk Order'
				}
				showBackBtn={true}
				size="xl"
				sx={{
					boxShadow: `0 0 20px 0px ${brand600}`,
					width: '840px',
					maxWidth: '95vw',
					height: 'auto',
					maxHeight: '90vh',
					overflowY: 'auto',
				}}
				isDirty={isBulkFormDirty}
				saveDraftAction={bulkOrderExitDraftAction}
			>
				<BulkOrderRequestForm
					mode={
						modalInformation.mode === 'editBulk'
							? 'edit'
							: modalInformation.mode === 'reorderBulk'
								? 'reorder'
								: 'create'
					}
					orderId={modalInformation.orderId}
					onSuccess={() => {
						setModalInformation({ mode: '' });
						refetch();
					}}
					onIsDirtyChange={setIsBulkFormDirty}
					onExitConfirmationDraftActionChange={
						['createBulk', 'editBulk', 'reorderBulk'].includes(
							modalInformation.mode,
						)
							? setBulkOrderExitDraftAction
							: undefined
					}
				/>
			</FaceliftDialogWithExitConfirmation>

			{/* Order Details Modal */}
			<FaceliftDialog
				open={modalInformation.mode === 'viewDetails'}
				onOpenChange={(openState) => {
					// Keep modal open during tutorial unless explicitly closed
					if (!openState && isRequestsTutorialActive) {
						return;
					}
					if (!openState) {
						setModalInformation({ mode: '' });
					}
				}}
				title="Details"
				showBackBtn={true}
				sx={{
					width: '1000px',
					maxWidth: '90vw',
					boxShadow: `0 0 20px 0px ${brand600}`,
				}}
			>
				<Show when={isOrderDetailsLoading}>
					<FaceliftDataLoader
						title="Loading order details..."
						description="Please wait while we fetch the latest order information."
					/>
				</Show>
				<Show when={!isOrderDetailsLoading}>
					<DetailsView
						data={
							orderDetails?.data
								? parseOrderDetailsForModal(orderDetails.data)
								: []
						}
						isLoading={false}
						gridConfig={{ base: '1fr', md: 'repeat(2, 1fr)' }}
					/>
				</Show>
			</FaceliftDialog>

			{/* Shipment Details Modal */}
			<FaceliftDialog
				open={modalInformation.mode === 'viewShipment'}
				onOpenChange={(openState) => {
					if (!openState) {
						setModalInformation({ mode: '' });
					}
				}}
				title="Order Tracking"
				showBackBtn={true}
				sx={{
					width: '1000px',
					maxWidth: '90vw',
					maxHeight: '85vh',
					boxShadow: `0 0 20px 0px ${brand600}`,
					overflowY: 'auto',
				}}
			>
				<ShipmentModalContent
					orderData={orderShipments}
					isLoading={isOrderShipmentsLoading}
				/>
			</FaceliftDialog>

			{/* Support Ticket Modal */}
			<FaceliftDialogWithExitConfirmation
				open={modalInformation.mode === 'supportTicket'}
				onOpenChange={(openState) => {
					if (!openState) {
						setModalInformation({ mode: '' });
						setIsSupportTicketFormDirty(false);
					}
				}}
				title="Create Support Ticket"
				showBackBtn={true}
				size="xl"
				sx={{
					boxShadow: `0 0 20px 0px ${brand600}`,
					width: '840px',
					maxWidth: '95vw',
					overflowY: 'auto',
				}}
				isDirty={isSupportTicketFormDirty}
				saveDraftAction={supportTicketExitDraftAction}
			>
				<SupportTicketForm
					mode="create"
					initialData={{
						clinic: modalInformation.clinicData || null,
						type: { value: '953870002', label: 'Orders' },
						subject: modalInformation.orderName || '',
						status: { value: '1', label: 'In Progress' },
					}}
					onSuccess={() => {
						setModalInformation({ mode: '' });
						setIsSupportTicketFormDirty(false);
						refetch();
					}}
					onIsDirtyChange={setIsSupportTicketFormDirty}
					onExitConfirmationDraftActionChange={
						modalInformation.mode === 'supportTicket'
							? setSupportTicketExitDraftAction
							: undefined
					}
				/>
			</FaceliftDialogWithExitConfirmation>
		</Box>
	);
};

export default RequestsTable;
